<template>
  <Main>
    <template v-slot:breadcrumb>
      <BaseBreadcrumb
        @return="previousPage"
        :items="breadcrumb"
        class="d-none d-sm-flex"
      />
    </template>
    <template v-slot:breadcrumb-mobile>
      <BaseBreadcrumb @return="previousPage" :items="breadcrumb" />
    </template>
    <v-form v-if="economicGroup && company" ref="form" @submit.prevent="submit">
      <NewCompanyForm
        :value="model"
        :balance-documents-model="balanceDocuments"
        :balance-error-messages="balanceFormErrors"
        :documents-model="otherDocuments"
        @open-balance-modal="onToggleBalanceModal"
        @open-single-document-modal="onToggleSingleDocumentModal"
        @preview-document="onPreviewDocument"
        @remove-document="onRemoveDocument"
        @preview-file="onPreviewFile"
        @remove-file="onRemoveFile"
        @remove-others-documents="onRemoveOthersDocuments"
      >
        <h3 class="gray--text mb-2">
          GRUPO ECONÔMICO: {{ economicGroup.nome }}
        </h3>
        <fragment v-if="isSubsidiary">
          <div class="d-flex mb-2">
            <v-switch
              v-model="model.ativo"
              class="pa-0 ma-0"
              color="secondary"
              inset
              hide-details
              dense
            />
            <p class="darkBluishGray--text caption mb-0">
              {{ isActivate }}
            </p>
          </div>
          <div>
            <SuggestInput
              v-model="tomador"
              :search-term="search"
              :options="companiesList"
              :label-text="textMatriz"
              textPlaceholder="Razão Social / CNPJ"
              v-bind="defaultFilterProps"
              @search="onSearchCompany"
            />
          </div>
          <v-divider />
        </fragment>
        <fragment v-else>
          <div class="d-flex mb-2">
            <v-switch
              v-model="model.ativo"
              class="pa-0 ma-0"
              color="secondary"
              inset
              hide-details
              dense
            />
            <p class="darkBluishGray--text caption mb-0">
              {{ isActivate }}
            </p>
          </div>
        </fragment>
        <FormSection
          :class="isSubsidiary && 'pt-9'"
          :title="companyDetailsTitle"
        >
          <CompanyDetailsFormSection :value="model" />
        </FormSection>
      </NewCompanyForm>
      <BaseFooter>
        <v-row>
          <v-spacer />
          <v-col class="pr-sm-5" cols="12" sm="3">
            <BaseButton
              class="error subtitle-2 font-weight-bold"
              width="100%"
              @click="previousPage"
              v-html="'CANCELAR'"
            />
          </v-col>
          <v-col cols="12" sm="3">
            <BaseButton
              type="submit"
              class="secondary text-uppercase subtitle-2 font-weight-bold"
              width="100%"
            >
              SALVAR
            </BaseButton>
          </v-col>
        </v-row>
      </BaseFooter>
    </v-form>
    <BalanceModal
      v-if="isBalanceModalOpen"
      v-model="isBalanceModalOpen"
      :form-model="selectBalanceDocumentModel"
      @selected-files="onSelectedBalanceDocuments"
      @preview-file="onPreviewFile"
      @remove-file="onRemoveFile"
      @submit="onCompletedBalanceDocumentSelection"
      @close="onToggleBalanceModal"
    />
    <SingleDocumentModal
      v-if="isDocumentModalOpen"
      v-model="isDocumentModalOpen"
      :title="documentModalTitle"
      :type-document="documentModalForKey"
      :current-documents="otherDocuments[documentModalForKey]"
      @remove-others-documents="onRemoveOthersDocuments"
      @submit="onCompletedDocumentSelection"
      @close="onToggleDocumentModal"
    />
    <ModalCancelCompany
      @close="closeModalCancel"
      v-model="modalCancel"
      title="SAIR SEM SALVAR"
      text="As alterações não foram salvas. Deseja realmente sair da tela?"
    />
  </Main>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import { LoadingRequest } from "@/mixins/LoadingMixin";
import EconomicGroupMixin from "../mixins/EconomicGroupMixin";
import BalanceModal from "../components/BalanceModal.vue";
import NewCompanyForm from "../components/NewCompanyForm.vue";
import SingleDocumentModal from "../components/SingleDocumentModal.vue";
import FormSection from "@/components/FormSection.vue";
import CompanyDetailsFormSection from "@/components/form/CompanyDetailsFormSection.vue";
import SuggestInput from "@/components/SuggestInput.vue";
import SuggestMixin from "@/mixins/SuggestMixin";
import CompanyMixin from "@/mixins/CompanyMixin";
import { normalizeCnpj } from "@/utils/documents.js";
import ModalCancelCompany from "../../insurance_company/components/ModalCancelInsurance";
const defaultFilterProps = {
  outlined: true,
  dense: true,
  "background-color": "white",
  class: "filter__icon",
  "append-outer-icon": "fa-search"
};

const successMessageBuilder = {
  tomador: (company, showTomador) => `${showTomador ? "Tomador ": ""}Matriz ${company.nome_fantasia} salvo`,
  subsidiaria: (company, showTomador) => `${showTomador ? "Tomador ": ""}Filial ${company.nome_fantasia} salva`
};

const BREADCRUMB_TOMADOR_FILIAL = [
  {
    text: "Tomadores e Filiais",
    to: "economic_group list"
  }
];

const BREADCRUMB_EMPRESAS_GRUPO = [
  {
    text: "Empresas do Grupo",
    to: "economic_group list"
  }
];

export default {
  name: "NewCompany",
  beforeRouteLeave(to, from, next) {
    if (this.cancel || this.formIsPristine()) next();
    this.modalCancel = true;
    this.nextRoute = to;
  },
  mixins: [LoadingRequest, EconomicGroupMixin, SuggestMixin, CompanyMixin],
  created(){
    this.insertValuesOnBreadcrumb();
  },
  components: {
    NewCompanyForm,
    BalanceModal,
    SingleDocumentModal,
    FormSection,
    CompanyDetailsFormSection,
    SuggestInput,
    ModalCancelCompany
  },
  data: () => ({
    search: "",
    defaultFilterProps,
    isBalanceModalOpen: false,
    model: {
      atividade_principal: {},
      endereco: {}
    },
    balanceDocuments: { documents: [] },
    breadcrumb: null,
    otherDocuments: {
      contrato_social: [],
      estatuto: [],
      ata: [],
      outros: []
    },
    documentModalForKey: null,
    companiesList: [],
    tomador: null,
    oldCNAE: null,
    cloneBalanceDocuments: {},
    cloneOtherDocuments: {},
    cancel: false,
    modalCancel: false,
    nextRoute: null,
    cloneForm: {}
  }),
  mounted() {
    const pipeline = !this.economicGroup
      ? this.findEconomicGroup(this.groupId)
      : Promise.resolve(this.economicGroup);
    pipeline.then(this.fetchData);
  },
  computed: {
    ...mapState("economic_group", ["economicGroup", "company"]),
    ...mapState("me", ["userAreaIsGarantia"]),
    textMatriz() {
      return this.userAreaIsGarantia ? "Tomador Matriz" : "Matriz";
    },
    isSubsidiary() {
      return this.company?.subsidiaria;
    },
    isActivate() {
      return this.isSubsidiary
        ? this.model.ativo
          ? "Ativa"
          : "Inativa"
        : this.model.ativo
        ? "Ativo"
        : "Inativo";
    },
    companyType() {
      return this.isSubsidiary ? "subsidiaria" : "tomador";
    },
    companyDetailsTitle() {
      if (this.userAreaIsGarantia) {
        return this.isSubsidiary
        ? "DADOS DO TOMADOR FILIAL"
        : "DADOS DO TOMADOR MATRIZ";
      }
      else {
        return this.isSubsidiary
        ? "DADOS DA FILIAL"
        : "DADOS DA MATRIZ";
      }
    },
    groupId() {
      return this.$route.params.groupId;
    },
    companyId() {
      return this.$route.params.companyId;
    },
    selectBalanceDocumentModel() {
      return this.balanceDocuments;
    },
    selectDocumentModel() {
      return this.otherDocuments;
    },
    formData() {
      const data = { ...this.model };
      if (this.isSubsidiary) {
        data.subsidiaria_de = this.tomador;
      }
      return data;
    },
    cloneModel() {
      return {
        ...this.model,
        atividade_principal: {
          ...this.model.atividade_principal
        },
        endereco: {
          ...this.model.endereco
        }
      };
    }
  },
  methods: {
    ...mapActions(["suggestCompany"]),
    ...mapActions("economic_group", [
      "findEconomicGroup",
      "findCNAE",
      "findCompany",
      "updateCompany",
      "findLegalPerson"
    ]),
    insertValuesOnBreadcrumb() {
      this.breadcrumb = this.userAreaIsGarantia ? BREADCRUMB_TOMADOR_FILIAL : BREADCRUMB_EMPRESAS_GRUPO;
    },
    updateModel(newModel) {
      this.model = newModel;
    },
    formIsPristine() {
      // Obrigado mask por facilitar minha vida ...
      return (
        _.isEqual(
          _.omit(this.model, ["cnpj", "endereco", "atividade_principal"]),
          _.omit(this.cloneForm, ["cnpj", "endereco", "atividade_principal"])
        ) &&
        _.isEqual(
          this.cloneBalanceDocuments,
          this.balanceDocuments.documents
        ) &&
        _.isEqual(
          _.omit(this.model.endereco, "cep"),
          _.omit(this.cloneForm.endereco, "cep")
        ) &&
        _.isEqual(
          _.omit(this.model.atividade_principal, "codigo"),
          _.omit(this.cloneForm.atividade_principal, "codigo")
        ) &&
        _.isEqual(this.cloneOtherDocuments, this.otherDocuments) &&
        normalizeCnpj(this.model.cnpj) ===
          normalizeCnpj(this.cloneForm.cnpj) &&
        this.model.endereco.cep.replace(/\D/g, "") ===
          this.cloneForm.endereco.cep.replace(/\D/g, "") &&
        this.model.atividade_principal.codigo.replace(/\D/g, "") ===
          this.cloneForm.atividade_principal.codigo.replace(/\D/g, "")
      );
    },
    closeModalCancel(value) {
      this.cancel = value;
      this.modalCancel = false;
      if (value) {
        this.$router.push({
          name: this.nextRoute.name,
          params: this.nextRoute.params
        });
      }
    },
    previousPage() {
      this.$router.push({
        name: "economic_group companies",
        params: { groupId: this.groupId }
      });
    },
    validate() {
      const hasBalanceDocuments = true;
      if (!hasBalanceDocuments) {
        this.balanceFormErrors = [
          "É preciso adicionar os arquivos de balanço."
        ];
      }
      return hasBalanceDocuments && this.$refs.form.validate();
    },
    onSearchCompany(search, loading) {
      this.performCompanySearch(
        search,
        this.groupId,
        loading,
        companies => (this.companiesList = companies),
        this
      );
    },
    submitCompany() {
      const { groupId, companyId, formData } = this;
      return this.submitDocuments(
        this.balanceDocuments,
        this.otherDocuments
      ).then(({ balancos, documentos }) =>
        this.updateCompany({
          groupId,
          companyId,
          info: {
            ...formData,
            documentos,
            balancos
          }
        })
      );
    },
    submit() {
      if (!this.validate()) {
        return;
      }

      this.withLoading(this.submitCompany()).then(company => {
        this.cancel = true;
        this.$router.push({
          name: "economic_group companies list",
          params: {
            groupId: this.groupId
          }
        });
        this.showAlertNotification({
          message: `${successMessageBuilder[this.companyType](
            company,
            this.userAreaIsGarantia
          )} com sucesso.`,
          type: "success"
        });
      });
    },
    makeLocalCopy(company) {
      this.oldCNAE =
        company.atividade_principal &&
        _.cloneDeep(company.atividade_principal.codigo);
      this.model = _.cloneDeep(company);
      this.model.atividade_principal = this.model.atividade_principal || {};
      this.model.atividade_principal.nome =
        !this.model.atividade_principal.nome &&
        company.atividade_principal &&
        _.cloneDeep(company.atividade_principal.descricao);
      this.tomador = this.model.subsidiaria_de;
      this.balanceDocuments.documents = company.balancos || [];
      company.documentos.forEach(document =>
        this.otherDocuments[document.tipo_documento.id].push(document)
      );
      this.cloneForm = _.cloneDeep(this.model);
      this.cloneBalanceDocuments = _.cloneDeep(this.balanceDocuments.documents);
      this.cloneOtherDocuments = _.cloneDeep(this.otherDocuments);
    },
    onRemoveOthersDocuments(item) {
      this.otherDocuments[item.tipo_documento.id] = this.otherDocuments[
        item.tipo_documento.id
      ].filter(document =>
        document.id
          ? document.id !== item.id
          : document.arquivo.nome !== item.arquivo.nome
      );
    },
    fetchData(economicGroup) {
      this.withLoading(
        this.findCompany({
          groupId: economicGroup.id,
          companyId: this.companyId
        }).then(this.makeLocalCopy)
      );
    }
  },
  watch: {
    "model.cnpj"(value, oldValue) {
      const cnpj = normalizeCnpj(value);
      if (!oldValue || cnpj === normalizeCnpj(oldValue)) {
        return;
      }
      if (cnpj.length === 14) {
        this.findLegalPersonProxy(cnpj);
      }
    },
    "model.atividade_principal.codigo"(value) {
      const cnae = value.replace(/\D/g, "");
      // Evitar de ficar num looping infinito
      if (cnae.length == 7 && this.oldCNAE != cnae) {
        this.findCNAEProxy(cnae);
      }
    }
  }
};
</script>
